import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { EQUIPMENT_LABEL, fmtMoney, fmtDateTime } from "../lib/format";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "", label: "All" },
];

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

export default function AdminIntake() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [modal, setModal] = useState(null); // { mode: 'approve'|'reject', itk }
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true); setDenied(false);
    api.get("/intake", { params: tab ? { status: tab } : {} })
      .then(r => setRows(r.data))
      .catch(e => { if (e.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openApprove = (itk) => {
    setErr("");
    setForm({
      rate: itk.target_rate || "",
      equipment_type: itk.equipment_type || "van",
      distance: itk.distance_mi || "",
      publish: true, notes: "",
    });
    setModal({ mode: "approve", itk });
  };
  const openReject = (itk) => { setErr(""); setForm({ reason: "" }); setModal({ mode: "reject", itk }); };

  const submitApprove = async () => {
    if (!form.rate || Number(form.rate) <= 0) { setErr("Enter a rate"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/intake/${modal.itk.intake_id}/approve`, {
        rate: Number(form.rate),
        equipment_type: form.equipment_type,
        distance: form.distance ? Number(form.distance) : null,
        notes: form.notes || null,
        publish: !!form.publish,
      });
      setModal(null); load();
    } catch (e) { setErr(e.response?.data?.detail || "Failed to approve"); }
    finally { setBusy(false); }
  };

  const submitReject = async () => {
    if (!form.reason?.trim()) { setErr("Enter a reason"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/intake/${modal.itk.intake_id}/reject`, { reason: form.reason });
      setModal(null); load();
    } catch (e) { setErr(e.response?.data?.detail || "Failed to reject"); }
    finally { setBusy(false); }
  };

  if (denied) {
    return (
      <div className="card-base p-10 text-center max-w-lg" data-testid="intake-denied">
        <h1 className="font-display text-2xl font-bold text-navy mb-2">Access restricted</h1>
        <p className="text-slate-500">The intake queue is available to operations and super admins only.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-intake-page">
      <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Brokerage</div>
      <h1 className="font-display text-4xl font-bold tracking-tighter text-navy mb-2">Intake queue</h1>
      <p className="text-slate-500 mb-6">Review incoming freight requests, set a rate, and publish to the load board.</p>

      <div className="flex gap-2 mb-6">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-navy text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
            data-testid={`intake-tab-${t.key || "all"}`}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 py-12 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-base p-12 text-center text-slate-400" data-testid="intake-empty">No requests here.</div>
      ) : (
        <div className="space-y-3" data-testid="intake-list">
          {rows.map(m => (
            <div key={m.intake_id} className="card-base p-5" data-testid={`intake-card-${m.intake_id}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display text-lg font-bold text-navy">{m.pickup_city}, {m.pickup_state} → {m.dropoff_city}, {m.dropoff_state}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_STYLE[m.status]}`}>{m.status}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{m.source}</span>
                  </div>
                  <div className="text-sm text-slate-600">
                    {EQUIPMENT_LABEL[m.equipment_type]}
                    {m.distance_mi ? ` · ${m.distance_mi} mi · ${m.eta_label || ""}` : ""}
                    {m.weight ? ` · ${Number(m.weight).toLocaleString()} lbs` : ""}
                    {m.commodity ? ` · ${m.commodity}` : ""}
                    {m.target_rate ? ` · target ${fmtMoney(m.target_rate)}` : ""}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {m.contact_name} · {m.contact_email}{m.contact_phone ? ` · ${m.contact_phone}` : ""} · {fmtDateTime(m.created_at)}
                  </div>
                  {m.notes && <div className="text-xs text-slate-500 mt-1 italic">“{m.notes}”</div>}
                  {m.status === "rejected" && m.rejection_reason && <div className="text-xs text-rose-600 mt-1">Rejected: {m.rejection_reason} (by {m.reviewed_by})</div>}
                  {m.status === "approved" && m.load_id && <Link to={`/app/loads/${m.load_id}`} className="text-xs font-semibold text-gold hover:underline mt-1 inline-block" data-testid={`intake-view-load-${m.intake_id}`}>View load {m.load_id} →</Link>}
                </div>
                {m.status === "pending" && (
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button onClick={() => openApprove(m)} className="btn-primary text-sm py-2 px-4" data-testid={`intake-approve-${m.intake_id}`}>Approve</button>
                    <button onClick={() => openReject(m)} className="btn-outline text-sm py-2 px-4" data-testid={`intake-reject-${m.intake_id}`}>Reject</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="intake-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModal(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            {modal.mode === "approve" ? (
              <>
                <h2 className="font-display text-xl font-bold text-navy mb-1">Approve & create load</h2>
                <p className="text-sm text-slate-500 mb-4">{modal.itk.pickup_city}, {modal.itk.pickup_state} → {modal.itk.dropoff_city}, {modal.itk.dropoff_state}</p>
                <div className="space-y-3">
                  <div>
                    <label className="label-base">Rate (USD)</label>
                    <input type="number" className="input-base" value={form.rate} onChange={e => setForm({...form, rate: e.target.value})} data-testid="approve-rate" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label-base">Equipment</label>
                      <select className="input-base" value={form.equipment_type} onChange={e => setForm({...form, equipment_type: e.target.value})} data-testid="approve-equipment">
                        {Object.entries(EQUIPMENT_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label-base">Distance (mi)</label>
                      <input type="number" className="input-base" value={form.distance} onChange={e => setForm({...form, distance: e.target.value})} data-testid="approve-distance" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={form.publish} onChange={e => setForm({...form, publish: e.target.checked})} data-testid="approve-publish" />
                    Publish to load board now (and alert nearby drivers)
                  </label>
                  <div>
                    <label className="label-base">Notes (optional)</label>
                    <input className="input-base" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="approve-notes" />
                  </div>
                </div>
                {err && <div className="text-red-600 text-sm mt-3" data-testid="modal-error">{err}</div>}
                <div className="flex gap-2 mt-5">
                  <button onClick={submitApprove} disabled={busy} className="btn-primary flex-1" data-testid="approve-confirm">{busy ? "Working…" : (form.publish ? "Approve & publish" : "Approve as draft")}</button>
                  <button onClick={() => setModal(null)} className="btn-outline" data-testid="approve-cancel">Cancel</button>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-display text-xl font-bold text-navy mb-1">Reject request</h2>
                <p className="text-sm text-slate-500 mb-4">{modal.itk.contact_name} will be notified.</p>
                <label className="label-base">Reason</label>
                <textarea className="input-base h-24" value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} data-testid="reject-reason" />
                {err && <div className="text-red-600 text-sm mt-3" data-testid="modal-error">{err}</div>}
                <div className="flex gap-2 mt-5">
                  <button onClick={submitReject} disabled={busy} className="btn-primary flex-1" data-testid="reject-confirm">{busy ? "Working…" : "Reject request"}</button>
                  <button onClick={() => setModal(null)} className="btn-outline" data-testid="reject-cancel">Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
